
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { trendlineStrategyService } from '../../../config/api';

// SVG Icons
const ChartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
);

const CodeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
);

const DuplicateIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
    </svg>
);

// Helper function to format date for display
const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const PublicStrategyList = ({ onCopyStrategy }) => {
    const [strategies, setStrategies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search State
    const [nameInput, setNameInput] = useState('');
    const [searchName, setSearchName] = useState('');
    const [idInput, setIdInput] = useState('');
    const [searchId, setSearchId] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0); // 0-indexed for API
    const [pageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const [selectedStrategy, setSelectedStrategy] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Debounce timer ref
    const debounceRef = useRef(null);

    const handleNameInput = (value) => {
        setNameInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchName(value);
            setCurrentPage(0);
        }, 400);
    };

    const handleIdInput = (value) => {
        setIdInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchId(value);
            setCurrentPage(0);
        }, 400);
    };

    const fetchStrategies = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await trendlineStrategyService.searchPublicStrategies({
                strategyId: searchId || undefined,
                name: searchName || undefined,
                page: currentPage,
                size: pageSize,
            });

            setStrategies(response.data || []);
            const meta = response.meta || {};
            setTotalPages(meta.totalPages || 0);
            setTotalItems(meta.totalItems || 0);
        } catch (err) {
            console.error('Error fetching public strategies:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load strategies. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchId, searchName, currentPage, pageSize]);

    useEffect(() => {
        fetchStrategies();
    }, [fetchStrategies]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const handleCopyClick = (e, strategy) => {
        e.stopPropagation();
        if (onCopyStrategy) {
            onCopyStrategy(strategy);
        }
    };

    const handleViewJson = (e, strategy) => {
        e.stopPropagation();
        setSelectedStrategy(strategy);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedStrategy(null);
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center p-8 h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="p-4">
                    <div className="alert alert-error">
                        <span>{error}</span>
                        <button className="btn btn-ghost btn-xs" onClick={() => setError(null)}>✕</button>
                    </div>
                </div>
            );
        }

        if (strategies.length === 0) {
            return (
                <div className="p-12 text-center bg-base-100 rounded-lg border border-dashed border-base-300">
                    <p className="text-lg font-medium text-base-content/60">No public strategies found</p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto px-4">
                <table className="table table-zebra w-full">
                    <thead>
                        <tr>
                            <th className="pl-0">Strategy ID</th>
                            <th>Name</th>
                            <th>Ticker</th>
                            <th>Date Range</th>
                            <th>Description</th>
                            <th>Updated</th>
                            <th className="pr-0 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {strategies.map((strategy) => (
                            <tr
                                key={strategy.id}
                                className="hover:bg-base-200 cursor-default h-16 group"
                            >
                                <td className="text-xs text-base-content/60 font-mono pl-0">{strategy.id}</td>
                                <td className="font-medium">{strategy.name}</td>
                                <td>
                                    {strategy.tickerName && (
                                        <div className="badge badge-ghost gap-1">
                                            <ChartIcon /> {strategy.tickerName}
                                        </div>
                                    )}
                                </td>
                                <td className="text-sm text-base-content/70">
                                    {strategy.startDate && strategy.endDate ? (
                                        `${formatDate(strategy.startDate)} - ${formatDate(strategy.endDate)}`
                                    ) : <span className="italic text-base-content/30">N/A</span>}
                                </td>
                                <td className="max-w-xs truncate text-base-content/60" title={strategy.description}>
                                    {strategy.description || <span className="italic text-base-content/30">No description</span>}
                                </td>
                                <td className="text-sm text-base-content/70">
                                    {formatDate(strategy.updatedAt)}
                                </td>
                                <td className="pr-0 text-right">
                                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            className="btn btn-square btn-ghost btn-sm"
                                            onClick={(e) => handleCopyClick(e, strategy)}
                                            title="Copy to My Strategies"
                                        >
                                            <DuplicateIcon />
                                        </button>
                                        <button
                                            className="btn btn-square btn-ghost btn-sm"
                                            onClick={(e) => handleViewJson(e, strategy)}
                                            title="View JSON"
                                        >
                                            <CodeIcon />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="card card-bordered border-base-300 bg-base-100 shadow-sm">
                <div className="px-4 py-4 border-b border-base-200 bg-base-100 rounded-t-xl space-y-3">
                    <div className="flex justify-between items-center">
                        <h3 className="font-bold text-lg">Public Strategies</h3>
                        <span className="badge badge-ghost">Total: {totalItems}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[200px] max-w-sm">
                            <input
                                type="text"
                                placeholder="Search by name..."
                                className="input input-bordered input-sm w-full pl-9"
                                value={nameInput}
                                onChange={(e) => handleNameInput(e.target.value)}
                            />
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Strategy ID..."
                            className="input input-bordered input-sm w-56"
                            value={idInput}
                            onChange={(e) => handleIdInput(e.target.value)}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination Controls */}
                {!loading && totalPages > 0 && (
                    <div className="p-4 flex justify-center border-t border-base-200 bg-base-100 rounded-b-xl">
                        <div className="join">
                            <button
                                className="join-item btn btn-sm"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                <ChevronLeftIcon />
                            </button>
                            <button className="join-item btn btn-sm no-animation cursor-default bg-base-100">
                                Page {currentPage + 1} of {totalPages}
                            </button>
                            <button
                                className="join-item btn btn-sm"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1}
                            >
                                <ChevronRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* JSON Viewer Modal */}
            {isModalOpen && selectedStrategy && createPortal(
                <dialog className="modal modal-open">
                    <div className="modal-box w-11/12 max-w-5xl h-[85vh] p-0 flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-base-300 bg-base-100">
                            <div>
                                <h3 className="font-bold text-lg">{selectedStrategy.name}</h3>
                                <p className="text-xs text-base-content/60 font-mono mt-1">{selectedStrategy.id}</p>
                            </div>
                            <button className="btn btn-sm btn-circle btn-ghost" onClick={handleCloseModal}>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4 bg-base-200">
                            <pre className="p-4 bg-base-100 rounded-lg text-sm font-mono whitespace-pre-wrap break-words border border-base-300 shadow-inner text-base-content">
                                <code>{JSON.stringify(selectedStrategy, null, 2)}</code>
                            </pre>
                        </div>

                        <div className="p-4 border-t border-base-300 bg-base-100 flex justify-end">
                            <button className="btn" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={handleCloseModal}>close</button>
                    </form>
                </dialog>,
                document.body
            )}
        </div>
    );
};

export default PublicStrategyList;
